import { FC, useState } from 'react';
import { ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import { cn } from '@new/lib';
import { Button } from '@new/common/ui/Button/Button';
import { isImageUrl } from '../../lib/helpers';
import { useVideoCallContext } from '../../../../../contexts/VideoCallProvider';
import { StudentIdDocuments } from './StudentIdDocuments';

interface ScreenshotItem {
  src: string;
  alt: string;
  timestamp?: string;
}

export const PhotosPanel: FC = () => {
  const { photos, videoCall } = useVideoCallContext();

  const idImage1 = videoCall?.student?.onboarding?.idImage1;
  const idImage2 = videoCall?.student?.onboarding?.idImage2;
  const screenshots: ScreenshotItem[] = photos
    .filter((photo) => photo.dataUrl && isImageUrl(photo.dataUrl))
    .map((photo, index) => ({
      src: photo.dataUrl,
      alt: `Screenshot ${index + 1}`,
      timestamp: photo.createdAt
        ? new Date(photo.createdAt).toLocaleTimeString()
        : `Screenshot ${index + 1}`,
    }));

  const [currentIndex, setCurrentIndex] = useState(0);
  const currentScreenshot = screenshots[currentIndex];

  if (screenshots.length === 0 && !idImage1?.url && !idImage2?.url) {
    return (
      <div className="flex flex-col items-center gap-2 justify-center h-64 p-4">
        <Camera className="w-12 h-12 text-(--neutral-white-NW400)" />
        <p className="text-(--neutral-white-NW300) font-semibold [font-size:var(--fs-l3)] m-0!">
          No screenshots available
        </p>
        <p className="text-(--neutral-white-NW300) [font-size:var(--fs-l4)] m-0!">
          Take screenshots to see them here
        </p>
      </div>
    );
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : screenshots.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < screenshots.length - 1 ? prev + 1 : 0));
  };

  return (
    <div className="flex flex-col gap-6 p-4">
      <StudentIdDocuments idImage1={idImage1} idImage2={idImage2} />

      {screenshots.length > 0 && (
        <>
          <div className="relative bg-muted rounded-lg overflow-hidden m-0!">
            <img
              src={currentScreenshot.src}
              alt={currentScreenshot.alt}
              className="w-full h-64 rounded-md!"
            />

            {screenshots.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToPrevious}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-background/80 hover:bg-background rounded-full! shadow-md cursor-pointer"
                  aria-label="Previous screenshot"
                >
                  <ChevronLeft size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-background/80 hover:bg-background rounded-full! shadow-md cursor-pointer"
                  aria-label="Next screenshot"
                >
                  <ChevronRight size={16} />
                </Button>
              </>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-col justify-center items-center gap-2">
              <p className="[font-size:var(--fs-l3)] font-semibold text-(--neutral-white-NW200) l3 m-0!">
                {currentScreenshot.alt}
              </p>
              {currentScreenshot.timestamp && (
                <p className="[font-size:var(--fs-l4)] text-muted-foreground l4 m-0!">
                  {currentScreenshot.timestamp}
                </p>
              )}
            </div>

            {screenshots.length > 1 && (
              <div className="flex justify-center gap-1.5">
                {screenshots.map((screenshot, index: number) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={cn(
                      'w-2 h-2 rounded-full! transition-colors cursor-pointer',
                      index === currentIndex
                        ? 'bg-(--neutral-white-NW400) border border-transparent'
                        : 'bg-(--neutral-white-NW800) border border-(--neutral-white-NW400)'
                    )}
                    aria-label={`Go to ${screenshot.alt}`}
                    title={screenshot.alt}
                  />
                ))}
              </div>
            )}

            <div className="flex justify-between items-center [font-size:var(--fs-l4)] text-muted-foreground l4">
              <span>
                {currentIndex + 1} of {screenshots.length}
              </span>
              <div className="flex gap-3">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-secondary"></div>
                  {screenshots.length} screenshot{screenshots.length > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
